import React, { useEffect, useMemo, useState } from 'react';
import './ContactUs.css';

function ContactUs({ isOpen, onClose, product }) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [customMessage, setCustomMessage] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState('');
    const [submissionSuccess, setSubmissionSuccess] = useState(false);

    const onboardingEndpointUrl = useMemo(() => {
        return process.env.REACT_APP_ONBOARDING_ENDPOINT_URL || '';
    }, []);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (!isOpen) return;

        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    // Reset form on open
    useEffect(() => {
        if (!isOpen) return;
        setFirstName('');
        setLastName('');
        setEmail('');
        setCompany('');
        setCustomMessage('');
        setIsSubmitting(false);
        setSubmissionError('');
        setSubmissionSuccess(false);
    }, [isOpen]);

    if (!isOpen) return null;

    const isEmailValid = (value) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    };

    const isFormValid =
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        isEmailValid(email) &&
        company.trim().length > 0 &&
        customMessage.trim().length > 0;

    const handleSubmit = async (event) => {
        event.preventDefault();

        setSubmissionError('');
        setSubmissionSuccess(false);

        if (!onboardingEndpointUrl) {
            setSubmissionError('Missing onboarding endpoint URL configuration.');
            return;
        }

        if (!isFormValid) {
            setSubmissionError('Please complete all fields with a valid email address.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(onboardingEndpointUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    email: email.trim(),
                    company: company.trim(),
                    product: product.trim(),
                    customMessage: customMessage.trim()
                })
            });

            const responseBodyText = await response.text();
            let responseBodyJson = null;

            try {
                responseBodyJson = JSON.parse(responseBodyText);
            } catch (error) {
                responseBodyJson = null;
            }

            if (!response.ok) {
                const apiMessage =
                    (responseBodyJson && (responseBodyJson.message || responseBodyJson.error)) ||
                    responseBodyText ||
                    'Request failed.';
                throw new Error(apiMessage);
            }

            setSubmissionSuccess(true);
            setFirstName('');
            setLastName('');
            setEmail('');
            setCompany('');
            setCustomMessage('');
        } catch (error) {
            setSubmissionError(error instanceof Error ? error.message : 'Failed to submit the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="contact-overlay"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="contact-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Contact us"
            >
                <button
                    className="close-contact-button"
                    onClick={onClose}
                    type="button"
                    aria-label="Close contact modal"
                >
                    ×
                </button>

                <div className="contact-modal-header">
                    <div className="contact-badge">Contact</div>
                    <h2>Enquire for custom solutions</h2>
                    <p className="contact-subtitle">
                        Tell us what you’re trying to achieve and we’ll suggest the best next step.
                    </p>
                </div>

                <div className="contact-form-shell">
                    {/* ID added here to link with the footer button */}
                    <form id="contact-form" className="contact-form" onSubmit={handleSubmit}>
                        <div className="contact-form-grid">
                            <label className="contact-field">
                                <span className="contact-field-label">First name</span>
                                <input
                                    className="contact-input"
                                    type="text"
                                    value={firstName}
                                    onChange={(event) => setFirstName(event.target.value)}
                                    autoComplete="given-name"
                                    required
                                />
                            </label>

                            <label className="contact-field">
                                <span className="contact-field-label">Last name</span>
                                <input
                                    className="contact-input"
                                    type="text"
                                    value={lastName}
                                    onChange={(event) => setLastName(event.target.value)}
                                    autoComplete="family-name"
                                    required
                                />
                            </label>

                            <label className="contact-field contact-field-full">
                                <span className="contact-field-label">Email</span>
                                <input
                                    className="contact-input"
                                    type="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    autoComplete="email"
                                    required
                                />
                            </label>

                            <label className="contact-field contact-field-full">
                                <span className="contact-field-label">Company</span>
                                <input
                                    className="contact-input"
                                    type="text"
                                    value={company}
                                    onChange={(event) => setCompany(event.target.value)}
                                    autoComplete="organization"
                                    required
                                />
                            </label>

                            <label className="contact-field contact-field-full">
                                <span className="contact-field-label">What are you looking to achieve?</span>
                                <textarea
                                    className="contact-textarea"
                                    value={customMessage}
                                    onChange={(event) => setCustomMessage(event.target.value)}
                                    rows={6}
                                    required
                                />
                            </label>
                        </div>

                        {submissionError && (
                            <div className="contact-alert contact-alert-error" role="alert">
                                {submissionError}
                            </div>
                        )}

                        {submissionSuccess && (
                            <div className="contact-alert contact-alert-success" role="status">
                                Thanks — we’ve received your message and will be in touch.
                            </div>
                        )}
                    </form>
                </div>

                <div className="contact-modal-footer" style={{ justifyContent: 'space-between' }}>

                    {/* Left Side: Contact Methods */}
                    <div className="contact-methods-group">
                        <a href="tel:+6421863279" className="contact-icon-btn" aria-label="Call us">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.05 12.05 0 0 0 .57 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.05 12.05 0 0 0 2.81.57A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            <span>Call</span>
                        </a>

                        <a href="mailto:whittendamon@gmail.com" className="contact-icon-btn" aria-label="Email us">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                            <span>Email</span>
                        </a>
                    </div>

                    {/* Right Side: Primary Action */}
                    <button
                        className="contact-primary-action"
                        type="submit"
                        form="contact-form"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending…' : 'Send enquiry'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ContactUs;